/**
 * @sazeo/schemas — Addon installation schemas
 *
 * Spec: specs/SAZEO_V2_ANATOMY.md §7 (Addon Platform)
 *
 * These schemas describe the wire shape of `/addons/installations` and
 * `/addons/marketplace` — they are *not* the manifest shape (that lives in
 * `@sazeo/addon-runtime/manifest`). Kept as plain Zod here so the SDK can
 * validate responses without pulling the runtime into browser bundles that
 * don't actually mount addons.
 */

import { z } from 'zod';

export const AddonInstallationSchema = z.object({
  id: z.string().uuid(),
  tenant_id: z.string().uuid(),
  addon_id: z.string().min(1),
  version: z.string().min(1),
  enabled: z.boolean(),
  installed_by: z.string().uuid(),
  installed_at: z.string(),
  settings: z.record(z.string(), z.unknown()).default({}),
  updated_at: z.string(),
});
export type AddonInstallation = z.infer<typeof AddonInstallationSchema>;

export const AddonInstallationCreateInputSchema = z.object({
  addon_id: z.string().min(1),
  version: z.string().min(1),
  settings: z.record(z.string(), z.unknown()).optional(),
});
export type AddonInstallationCreateInput = z.infer<typeof AddonInstallationCreateInputSchema>;

export const AddonInstallationUpdateInputSchema = z.object({
  enabled: z.boolean().optional(),
  settings: z.record(z.string(), z.unknown()).optional(),
});
export type AddonInstallationUpdateInput = z.infer<typeof AddonInstallationUpdateInputSchema>;

/**
 * Marketplace listing — trimmed-down view of a manifest that's safe to
 * publish without pulling the full manifest schema client-side.
 */
export const AddonMarketplaceEntrySchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  description: z.string().min(1),
  category: z.string().min(1),
  version: z.string().min(1),
  vendor: z.string().min(1),
  icon: z.string().optional(),
  pricing: z.object({
    model: z.enum(['flat', 'per_seat', 'metered']),
    price_usd: z.number().nonnegative(),
    unit: z.enum(['month', 'year', 'event']),
    trial_days: z.number().int().nonnegative(),
  }),
});
export type AddonMarketplaceEntry = z.infer<typeof AddonMarketplaceEntrySchema>;
